<!-- src/views/Dashboard.vue -->
<template>
  <div class="page-dashboard">
    <div class="columns is-multiline">
      <div class="column is-12">
        <h1 class="title">Dashboard</h1>
      </div>

      <!-- Quick stats (counts) -->
      <div class="column is-3" v-for="s in quickStats" :key="s.key">
        <div class="box has-text-centered">
          <p class="heading">{{ s.label }}</p>
          <p class="title is-3">
            <span v-if="!loading">{{ s.value }}</span>
            <span v-else class="has-text-grey">...</span>
          </p>
        </div>
      </div>

      <!-- Latest Documents -->
      <div class="column is-6">
        <div class="card">
          <header class="card-header">
            <p class="card-header-title">Latest documents</p>
          </header>
          <div class="card-content">
            <div v-if="loading" class="has-text-grey">Loading…</div>
            <table v-else class="table is-fullwidth is-hoverable">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Type</th>
                  <th>Client</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="d in latestDocuments.slice(0, 10)"
                  :key="d.id"
                  @click="goToDocument(d.id)"
                  style="cursor: pointer"
                >
                  <td>{{ d.number || d.id }}</td>
                  <td class="td-ellipsis" :title="d.document_type_display">
                    {{ d.document_type_display }}
                  </td>
                  <td
                    class="td-ellipsis"
                    :title="d.third_party_name || d.third_party?.name"
                  >
                    {{ d.third_party_name || d.third_party?.name }}
                  </td>
                  <td>
                    {{ fmtMoney(d.total_ttc ?? d.gross_amount ?? d.total) }}
                  </td>
                </tr>
                <tr v-if="!latestDocuments.length">
                  <td colspan="5" class="has-text-grey has-text-centered">
                    No documents
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <footer class="card-footer">
            <router-link class="card-footer-item" to="dashboard/documents">
              View all
            </router-link>
          </footer>
        </div>
      </div>

      <!-- Latest Inventory Movements -->
      <div class="column is-6">
        <div class="card">
          <header class="card-header">
            <p class="card-header-title">Latest inventory movements</p>
          </header>
          <div class="card-content">
            <div v-if="loading" class="has-text-grey">Loading…</div>
            <table v-else class="table is-fullwidth is-hoverable">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Qty</th>
                  <th>Direction</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="m in latestMovements.slice(0, 10)" :key="m.id">
                  <td class="td-ellipsis" :title="m.item_name || m.item?.name">
                    {{ m.item_name || m.item?.name }}
                  </td>
                  <td>{{ m.quantity }}</td>
                  <td>{{ (m.coefficient ?? 0) > 0 ? 'IN' : 'OUT' }}</td>
                  <td>{{ fmtDate(m.created_at) }}</td>
                </tr>
                <tr v-if="!latestMovements.length">
                  <td colspan="4" class="has-text-grey has-text-centered">
                    No movements
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <footer class="card-footer">
            <router-link class="card-footer-item" to="dashboard/inventory">
              View all
            </router-link>
          </footer>
        </div>
      </div>

      <!-- Latest Clients -->
      <div class="column is-6">
        <div class="card">
          <header class="card-header">
            <p class="card-header-title">New clients</p>
          </header>
          <div class="card-content">
            <div v-if="loading" class="has-text-grey">Loading…</div>
            <table v-else class="table is-fullwidth is-hoverable">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="tp in latestThirdParties.slice(0, 10)"
                  :key="tp.id"
                  @click="goToThirdParty(tp.id)"
                  style="cursor: pointer"
                >
                  <td class="td-ellipsis" :title="tp.display_name || tp.name">
                    {{ tp.display_name || tp.name }}
                  </td>
                  <td class="td-ellipsis" :title="tp.email">
                    {{ tp.email }}
                  </td>
                  <td>{{ fmtDate(tp.created_at) }}</td>
                </tr>
                <tr v-if="!latestThirdParties.length">
                  <td colspan="3" class="has-text-grey has-text-centered">
                    No clients
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <footer class="card-footer">
            <router-link class="card-footer-item" to="dashboard/third-parties">
              View all
            </router-link>
          </footer>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
// Simple Dashboard using existing API endpoints, no backend changes.
// All comments in English as requested.

import api from '@/utils/axios';

export default {
  name: 'DashboardView',
  data() {
    return {
      loading: false,

      // Lists
      latestDocuments: [],
      latestMovements: [],
      latestThirdParties: [],

      // Counts (from DRF pagination "count")
      counts: {
        documents: 0,
        documents_open: 0, // optional: you may compute differently
        third_parties: 0,
        movements_30d: 0, // optional approximation
      },
    };
  },
  computed: {
    quickStats() {
      return [
        { key: 'documents', label: 'Documents', value: this.counts.documents },
        {
          key: 'third_parties',
          label: 'Clients',
          value: this.counts.third_parties,
        },
        {
          key: 'movements_30d',
          label: 'Mouvements (30j)',
          value: this.counts.movements_30d,
        },
        {
          key: 'documents_open',
          label: 'Ouverts',
          value: this.counts.documents_open,
        },
      ];
    },
  },
  methods: {
    async fetchAll() {
      // Fetch small slices from existing endpoints (same style as your other pages)
      this.loading = true;
      try {
        // Documents: latest 5
        const [docs, tps, movs] = await Promise.all([
          api.get('/documents/', {
            params: { ordering: '-updated_at', page_size: 5 },
          }),
          api.get('/third-parties/', {
            params: { ordering: '-created_at', page_size: 5 },
          }),
          api
            .get('/stock-movements/', {
              params: { ordering: '-created_at', page_size: 5 },
            })
            .catch(() => ({ data: { results: [], count: 0 } })),
        ]);

        // Lists
        this.latestDocuments = docs.data.results || docs.data; // handle both paginated and non-paginated
        this.latestThirdParties = tps.data.results || tps.data;
        this.latestMovements = movs.data.results || movs.data;

        // Counts (use DRF "count" when available; else fallback to list length)
        this.counts.documents = Number(
          docs.data.count ?? (this.latestDocuments?.length || 0)
        );
        this.counts.third_parties = Number(
          tps.data.count ?? (this.latestThirdParties?.length || 0)
        );

        // Approx: movements last 30d (if your endpoint supports filter, adapt here)
        this.counts.movements_30d = Number(
          movs.data.count ?? (this.latestMovements?.length || 0)
        );

        // Optional: open documents (adapt the filter to your API)
        // If you have a status field (e.g., status=open), you can query it:
        // const docsOpen = await api.get('/documents/', { params: { status: 'open', page_size: 1 } });
        // this.counts.documents_open = Number(docsOpen.data.count ?? 0);
        this.counts.documents_open = 0; // neutral default
      } finally {
        this.loading = false;
      }
    },
    fmtDate(isoStr) {
      if (!isoStr) return '';
      const d = new Date(isoStr);
      return d.toLocaleDateString();
    },
    fmtMoney(value) {
      if (value == null) return '';
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: 'EUR',
        }).format(value);
      } catch {
        return Number(value).toFixed(2);
      }
    },
    goToDocument(id) {
      // Align with your existing route name
      this.$router.push({ name: 'DetailDocument', params: { id } });
    },
    goToThirdParty(id) {
      this.$router.push({ name: 'ThirdPartyView', params: { id } });
    },
  },
  async mounted() {
    await this.fetchAll();
  },
};
</script>

<style scoped>
.table tbody tr:hover {
  background: rgba(0, 0, 0, 0.03);
}
/* Truncate long table cell content with ellipsis */
.td-ellipsis {
  max-width: 180px; /* adjust per column if needed */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
